import { useQuery, type QueryKey } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { getMetricsBatch } from '@/features/metrics/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { CLONE_TABLE_ROW_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { buildTagColorByName, mergeTagColorByName } from '@/features/shared/tags';
import { DeleteSuiteDialog } from '@/features/suites/components/dialogs';
import { suitesGridConfig } from '@/features/suites/grid/gridConfig';
import { queryKeys, useTagOptions } from '@/shared/hooks';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type DataGridProps, type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildSuitesColumns } from './suitesColumns';
import { useSuitesTableRowActions } from './useSuitesTableRowActions';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  QueryResult,
  SuiteExecutionStatus,
  SuiteListItemDto,
} from '@flowqa/shared';

type SuitesTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
  queryKey?: QueryKey;
  initialData?: QueryResult<SuiteListItemDto>;
  onRowsChange?: (rows: SuiteListItemDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: SuiteListItemDto[] } | null) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  onStatusSelect?: (status: SuiteExecutionStatus | null | undefined) => void;
  showDeltas?: boolean;
  tokenOverride?: string | null;
  disableLinks?: boolean;
  search?: string;
  sparklineRangeDays?: 7 | 14 | 30;
  metricsStartDay?: string | null;
  metricsEndDay?: string | null;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
  metricSeriesBySuiteIdOverride?: Map<
    string,
    {
      dates: Date[];
      passRate: number[];
      frequencyPerDay: number[];
      averageDurationMs: number[];
    }
  >;
  tagColorByNameOverride?: Partial<Record<string, TagColor>>;
};

const SuitesTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = false,
  disableColumnMenu = false,
  showToolbar = true,
  noRowsText = 'No suites found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  initialData,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  onStatusSelect,
  showDeltas = true,
  tokenOverride,
  disableLinks = false,
  search,
  sparklineRangeDays = 7,
  metricsStartDay,
  metricsEndDay,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  metricSeriesBySuiteIdOverride,
  tagColorByNameOverride,
}: SuitesTableProps): ReactElement => {
  const { session } = useAuth();
  const resolvedToken = tokenOverride ?? session?.tokens.accessToken ?? null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const [visibleRows, setVisibleRows] = useState<SuiteListItemDto[]>([]);
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const resolvedTagColorByName = useMemo(
    () => mergeTagColorByName(tagColorByName, tagColorByNameOverride),
    [tagColorByName, tagColorByNameOverride],
  );
  const {
    deleteTarget,
    deleteLoading,
    deleteRowPendingId,
    updateRowTagsColorPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
  } = useSuitesTableRowActions({
    resolvedToken,
    selectedProjectId,
  });
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.suites]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const metricsRange = sparklineRangeDays === 14 ? '14d' : sparklineRangeDays === 30 ? '30d' : '7d';
  const metricsRangeKey =
    metricsStartDay && metricsEndDay ? `${metricsStartDay}_${metricsEndDay}` : 'all';
  const shouldQueryPerRowMetrics =
    resolvedToken !== null && metricSeriesBySuiteIdOverride === undefined;
  const metricsEntityKey = useMemo(
    () => visibleRows.map((row) => `${row.projectId}:${row.id}`).join(','),
    [visibleRows],
  );
  const metricsBatchQuery = useQuery({
    queryKey: queryKeys.metricsBatch(
      selectedOrganisationId ?? null,
      'suite',
      metricsRange,
      metricsEntityKey,
      metricsRangeKey,
    ),
    enabled: shouldQueryPerRowMetrics && visibleRows.length > 0,
    staleTime: 30_000,
    queryFn: async () =>
      await getMetricsBatch(
        {
          range: metricsRange,
          scope: 'suite',
          startDay: metricsStartDay ?? null,
          endDay: metricsEndDay ?? null,
          entities: visibleRows.map((row) => ({ entityId: row.id, projectId: row.projectId })),
        },
        selectedOrganisationId ?? undefined,
        resolvedToken,
      ),
  });
  const metricSeriesBySuiteId = useMemo(() => {
    const metricsBySuiteId = new Map(
      (metricsBatchQuery.data ?? []).map((item) => [item.entityId, item.metrics]),
    );
    const result = new Map<
      string,
      {
        dates: Date[];
        passRate: number[];
        frequencyPerDay: number[];
        averageDurationMs: number[];
      }
    >();
    visibleRows.forEach((row) => {
      const queryData = metricsBySuiteId.get(row.id);
      if (!queryData) return;
      const dates = queryData.metricsRangeDates.map((day) => dayjs(day).toDate());
      const passRate = queryData.series.filteredPassRate.map((point) => point.value);
      const frequencyPerDay = queryData.series.executionTrendMetrics.map((point) => point.value);
      const averageDurationMs = queryData.series.executionDurationAverage.map(
        (point) => point.value * 1000,
      );
      if (
        dates.length === 0 ||
        passRate.length !== dates.length ||
        frequencyPerDay.length !== dates.length ||
        averageDurationMs.length !== dates.length
      ) {
        return;
      }
      result.set(row.id, { dates, passRate, frequencyPerDay, averageDurationMs });
    });
    return result;
  }, [metricsBatchQuery.data, visibleRows]);
  const resolvedMetricSeriesBySuiteId = metricSeriesBySuiteIdOverride ?? metricSeriesBySuiteId;
  const metricsQueriesReady =
    !shouldQueryPerRowMetrics ||
    visibleRows.length === 0 ||
    metricsBatchQuery.data !== undefined ||
    (!metricsBatchQuery.isPending && !metricsBatchQuery.isFetching);
  const gridConfig = suitesGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'suites-table-clone' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
  });

  const suitesQueryKey = queryKey ?? gridConfig.queryKey;
  const defaultColumns = useMemo(
    () =>
      buildSuitesColumns({
        selectedProjectId,
        onProjectSelect: setSelectedProjectId,
        onStatusSelect,
        thresholds: thresholds.suites,
        resolveThresholds,
        showDeltas,
        disableLinks,
        latestAsMeta: true,
        includeTagsInNameColumn: true,
        includeActionsColumn: true,
        includeLatestTestsColumn: true,
        includeLastExecutedColumn: false,
        metricSeriesBySuiteId: resolvedMetricSeriesBySuiteId,
        onDeleteRow,
        deleteRowPendingId,
        tagColorByName: resolvedTagColorByName,
        onUpdateRowTagsColor,
        updateRowTagsColorPendingId,
      }),
    [
      disableLinks,
      deleteRowPendingId,
      onDeleteRow,
      onStatusSelect,
      onUpdateRowTagsColor,
      resolveThresholds,
      resolvedMetricSeriesBySuiteId,
      resolvedTagColorByName,
      suitesQueryKey,
      setSelectedProjectId,
      showDeltas,
      thresholds.suites,
      updateRowTagsColorPendingId,
    ],
  );
  const togglableColumnFields = useMemo(() => {
    const helperOnlyFields = new Set(['latestExecutionTimestamp']);
    return defaultColumns
      .map((column) => column.field)
      .filter((field) => !helperOnlyFields.has(field));
  }, [defaultColumns]);

  return (
    <>
      <ServerDataTable<SuiteListItemDto>
        queryPath="suites/query"
        {...gridConfig}
        queryFn={queryFn ?? gridConfig.queryFn}
        queryKey={queryKey ?? gridConfig.queryKey}
        initialData={initialData}
        columns={defaultColumns}
        getRowId={(row) => row.id}
        token={resolvedToken}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        disableCellFocus
        getRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        showSkeletonOnFetch
        showLastUpdated={showLastUpdated}
        autoHeight
        externalLoading={!metricsQueriesReady}
        onRowsChange={(rows) => {
          setVisibleRows(rows);
          onRowsChange?.(rows);
        }}
        onQueryMeta={onQueryMeta}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        gridProps={{
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={tableSx}
      />
      <DeleteSuiteDialog
        open={deleteTarget !== null}
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};

export const SuitesTable = memo(SuitesTableComponent);
